import { AnimatePresence, motion } from "motion/react";
import { bool, func, node, number, oneOf, string } from "prop-types";
import { Children, useState } from "react";

import { useAnimationStyles } from "./stateHooks";

const Padding = motion.div;

const StaggerMotion = ({
  children,
  delayIn,
  delayOut,
  setHasEntered,
  direction,
  variant,
  snapHeight,
  quickExit,
}) => {
  // pluck out `paddingTransition` as unused, since it is not a valid
  // `motion` prop and so causes a React warning
  const { paddingTransition: _paddingTransition, ...animationStyles } =
    useAnimationStyles({
      direction,
      delayIn,
      delayOut,
      variant,
      snapHeight,
      quickExit,
    });
  return (
    <motion.div
      {...animationStyles}
      onAnimationComplete={() => setHasEntered(true)}
    >
      {children}
    </motion.div>
  );
};

StaggerMotion.propTypes = {
  children: node,
  delayIn: number,
  delayOut: number,
  setHasEntered: func.isRequired,
  direction: oneOf(["top", "bottom"]),
  variant: string,
  snapHeight: bool,
  quickExit: bool,
};

/**
 * @param {import("./types").FnrStaggeredEntryProps} props
 * @returns {JSX.Element|null}
 */
const FnrStaggeredEntry = ({
  interval = 0.05,
  delay = 0,
  show,
  muted,
  initOnly,
  noInit,
  gap,
  children,
  direction,
  variant = "scale",
  snapHeight,
  quickExit,
}) => {
  const [hasEntered, setHasEntered] = useState();
  // Spacer transition shares the card's exit source so the padding collapse
  // stays in lockstep with the dying card's height collapse.
  const { paddingTransition } = useAnimationStyles({
    direction,
    variant,
    quickExit,
  });

  if (!children) {
    return null;
  }

  if (muted || (initOnly && hasEntered)) {
    return children;
  }

  return (
    <AnimatePresence initial={!noInit}>
      {show
        ? Children.map(children, (child, idx) => {
            const isChild = Boolean(child);
            const delayIn = interval * idx + delay;
            const delayOut = interval * Math.abs(idx - (children.length - 1));
            return (
              <StaggerMotion
                key={child?.key || idx}
                delayIn={delayIn}
                delayOut={delayOut}
                setHasEntered={setHasEntered}
                direction={direction}
                variant={variant}
                snapHeight={snapHeight}
                quickExit={quickExit}
              >
                {gap > 0 && isChild ? (
                  <Padding
                    initial={false}
                    animate={{ height: idx === 0 ? 0 : gap / 2 }}
                    transition={paddingTransition}
                  />
                ) : null}
                {child}
                {gap > 0 && isChild ? (
                  <Padding
                    initial={false}
                    animate={{
                      height: idx === children.length - 1 ? 0 : gap / 2,
                    }}
                    transition={paddingTransition}
                  />
                ) : null}
              </StaggerMotion>
            );
          })
        : null}
    </AnimatePresence>
  );
};

FnrStaggeredEntry.propTypes = {
  children: node,
  delay: number,
  interval: number,
  show: bool,
  muted: bool,
  initOnly: bool,
  noInit: bool,
  gap: number,
  snapHeight: bool,
  quickExit: bool,
  direction: oneOf(["top", "bottom"]),
  variant: oneOf(["scale", "fade", "opacity", "gentle"]),
};

export default FnrStaggeredEntry;
